import React, { useState, useMemo } from 'react'
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  Description as DocxIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { backendClient } from '../../../api/backend'

/**
 * ArtifactPreview — right-side panel that renders a styled preview
 * of AI response content with download options.
 */
/**
 * Extract table blocks (with optional preceding heading) from markdown.
 * Returns an array of markdown strings, each containing one heading + table.
 */
const extractTableBlocks = (md) => {
  const tableRegex = /(?:^[ \t]*#{1,6}\s+.+$\n(?:\s*\n)*)?(?:^\|.+\|[ \t]*$\n^\|[\s:|-]+\|[ \t]*$\n(?:^\|.+\|[ \t]*$\n?)+)/gm
  const matches = md.match(tableRegex)
  return matches || []
}

const ArtifactPreview = ({ content, title: titleProp, artifactType, onClose }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [exportAnchorEl, setExportAnchorEl] = useState(null)
  const [exporting, setExporting] = useState(null)

  const handleExport = async (format) => {
    setExportAnchorEl(null)
    setExporting(format)
    try {
      const blob = await backendClient.exportArtifact(content, format, titleProp)
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `lawis-export.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExporting(null)
    }
  }

  const markdownComponents = {
    p: ({ children }) => (
      <Typography component="p" sx={{ my: 0.75, fontSize: '0.9rem', lineHeight: 1.7 }}>
        {children}
      </Typography>
    ),
    h1: ({ children }) => (
      <Typography variant="h5" component="h1" sx={{ mt: 2.5, mb: 1, fontWeight: 700, fontSize: '1.4rem' }}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="h6" component="h2" sx={{ mt: 2, mb: 0.75, fontWeight: 600, fontSize: '1.15rem' }}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="subtitle1" component="h3" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600, fontSize: '1rem' }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: 2.5, my: 0.75, listStyleType: 'disc', fontSize: '0.9rem' }}>{children}</Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 2.5, my: 0.75, listStyleType: 'decimal', fontSize: '0.9rem' }}>{children}</Box>
    ),
    li: ({ children }) => (
      <Box component="li" sx={{ mb: 0.3, fontSize: '0.9rem' }}>{children}</Box>
    ),
    strong: ({ children }) => (
      <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>
    ),
    code: ({ inline, children }) => {
      if (inline) {
        return (
          <Box
            component="code"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              px: 0.5,
              py: 0.15,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.8em',
            }}
          >
            {children}
          </Box>
        )
      }
      return (
        <Box
          component="pre"
          sx={{
            bgcolor: '#1c1917',
            color: '#e7e5e4',
            p: 1.5,
            borderRadius: 1.5,
            overflow: 'auto',
            my: 1,
            fontSize: '0.8rem',
            lineHeight: 1.5,
          }}
        >
          <Box component="code" sx={{ fontFamily: 'monospace' }}>{children}</Box>
        </Box>
      )
    },
    table: ({ children }) => (
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          width: '100%',
          my: 1.5,
          fontSize: '0.85rem',
          '& th, & td': {
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            px: 1.25,
            py: 0.6,
            textAlign: 'left',
          },
          '& th': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            fontWeight: 600,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
          },
          '& tr:nth-of-type(even)': {
            bgcolor: alpha(theme.palette.action.hover, 0.4),
          },
        }}
      >
        {children}
      </Box>
    ),
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          pl: 1.5,
          my: 1,
          color: theme.palette.text.secondary,
          fontStyle: 'italic',
          fontSize: '0.9rem',
        }}
      >
        {children}
      </Box>
    ),
    hr: () => (
      <Box component="hr" sx={{ border: 'none', borderTop: `1px solid ${theme.palette.divider}`, my: 2 }} />
    ),
    a: ({ href, children }) => (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
      >
        {children}
      </Box>
    ),
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }} noWrap>
          {titleProp || t('export')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Download button */}
          <Tooltip title={t('export')}>
            <IconButton
              size="small"
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              disabled={!!exporting}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
              }}
            >
              {exporting ? <CircularProgress size={18} /> : <FileDownloadIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Close button */}
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Export dropdown menu */}
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={() => setExportAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: { minWidth: 160, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
            },
          }}
        >
          {artifactType !== 'spreadsheet' && (
            <MenuItem onClick={() => handleExport('pdf')}>
              <ListItemIcon><PdfIcon fontSize="small" sx={{ color: '#e53935' }} /></ListItemIcon>
              <ListItemText>{t('export_pdf')}</ListItemText>
            </MenuItem>
          )}
          {artifactType !== 'spreadsheet' && (
            <MenuItem onClick={() => handleExport('docx')}>
              <ListItemIcon><DocxIcon fontSize="small" sx={{ color: '#1976d2' }} /></ListItemIcon>
              <ListItemText>{t('export_docx')}</ListItemText>
            </MenuItem>
          )}
          {(!artifactType || artifactType === 'table' || artifactType === 'spreadsheet') && (
            <MenuItem onClick={() => handleExport('xlsx')}>
              <ListItemIcon><ExcelIcon fontSize="small" sx={{ color: '#2e7d32' }} /></ListItemIcon>
              <ListItemText>{t('export_xlsx')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Preview content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2.5,
          py: 2,
          '& > *:first-child': { mt: 0 },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {content}
        </ReactMarkdown>
      </Box>
    </Box>
  )
}

export default ArtifactPreview
